"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Play, Sparkles } from "lucide-react";
import { MAISON, IMG } from "@/lib/diwline-data";

export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacityText = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);

  return (
    <section
      ref={ref}
      id="accueil"
      className="relative flex min-h-[100svh] items-center justify-center overflow-hidden"
      aria-label="Accueil — DIW'LINE, l'or liquide du Sénégal"
    >
      {/* Fond parallaxe */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10 scale-110">
        <img
          src={`${IMG}/hero.jpg`}
          alt="Champs dorés du Sénégal à l'aube"
          className="h-full w-full object-cover animate-kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/72 via-royal/58 to-royal" />
        <div className="absolute inset-0 bg-royal-vignette" />
      </motion.div>

      {/* Cadre ornemental */}
      <div className="pointer-events-none absolute inset-3 md:inset-6 border border-gold/25 z-0" aria-hidden />
      <div className="pointer-events-none absolute inset-5 md:inset-8 border border-gold/10 z-0" aria-hidden />

      <motion.div
        style={{ opacity: opacityText, y: yText }}
        className="relative z-10 mx-auto max-w-4xl px-6 pt-24 pb-28 text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mx-auto flex w-fit items-center gap-3 rounded-full border border-gold/40 bg-black/35 px-5 py-2 backdrop-blur-sm"
        >
          <Sparkles className="h-3.5 w-3.5 text-gold" aria-hidden />
          <span className="text-[11px] md:text-xs uppercase tracking-[0.35em] text-gold-light">
            {MAISON.fondation} · {MAISON.ville}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.35 }}
          className="mt-8 font-display text-4xl sm:text-5xl md:text-7xl font-bold leading-[1.08]"
        >
          <span className="text-gold-gradient animate-shimmer">L&apos;Or Liquide</span>
          <br />
          <span className="text-cream">du Sénégal</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="mt-6 text-base md:text-lg leading-relaxed text-cream/85 max-w-2xl mx-auto"
        >
          Huilleries <span className="text-gold font-semibold">DIW&apos;LINE</span> — patrimoine royal
          d&apos;excellence, souveraineté nourricière et émancipation féminine. De nos champs dorés
          aux tables du monde.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.75 }}
          className="mt-5 font-elegant italic text-lg md:text-xl text-gold-light"
        >
          « {MAISON.devise} »
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="#produits"
            className="group inline-flex min-h-12 items-center gap-2 rounded-sm bg-gradient-to-r from-gold-light via-gold to-gold-deep px-8 py-3.5 text-sm font-bold uppercase tracking-widest text-royal shadow-[0_10px_35px_rgba(212,175,55,0.35)] transition-transform hover:scale-[1.03]"
          >
            Découvrir les Huiles
            <ChevronDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" aria-hidden />
          </a>
          <a
            href="#film"
            className="inline-flex min-h-12 items-center gap-3 rounded-sm border border-gold/60 px-8 py-3.5 text-sm font-semibold uppercase tracking-widest text-gold transition-colors hover:bg-gold/10"
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gold/70" aria-hidden>
              <Play className="h-3 w-3 fill-gold text-gold" />
            </span>
            Visionner le film royal · 2:45
          </a>
        </motion.div>

        <motion.blockquote
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.2 }}
          className="mx-auto mt-12 max-w-3xl border-t border-gold/20 pt-6"
        >
          <p className="text-xs md:text-sm italic leading-relaxed text-cream/60">
            « {MAISON.recommandation} »
          </p>
          <cite className="mt-2 block text-[11px] uppercase not-italic tracking-[0.3em] text-gold/70">
            Son Excellence le Président Ndongo NDIAYE
          </cite>
        </motion.blockquote>
      </motion.div>

      <motion.a
        href="#or-liquide"
        aria-label="Défiler vers la suite"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ opacity: { delay: 1.6, duration: 1 }, y: { repeat: Infinity, duration: 2.2, ease: "easeInOut" } }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-gold"
      >
        <ChevronDown className="h-7 w-7" />
      </motion.a>
    </section>
  );
}
